import _ from 'lodash';
import { AIECGViewer, CAPTURE_MODE } from '@/shared/ui';
import { EVENT_OTHER_TYPE } from '@/entities/event';
import { BEAT_HR_TAB_ID } from '@/entities/report';
import { LOAD_BEAT_STATUS } from '@/shared/model';
import BeatEcgViewerSkeleton from './beatEcgViewerSkeleton';
import NavigatorContainer from './navigatorContainer';
import useBeatEcgViewerWrapper from './useBeatEcgViewerWrapper';
import './styles.scss';

const configChart = {
  isDisplayScrollbar: false,
  isEdit: true,
};

export type BeatEcgViewerWraperProps = {
  ecgDataMap: any;
  studyId: string;
  profileId: string;
  timezoneOffset: number;
  startReport: any;
  stopReport: any;
  callbackListDate: (listDate: any[]) => void;
};

/**
 * Renders the ECG viewer for R-R heatmap beat review, showing the strip around the currently displayed beat. Falls back to a skeleton while loading, when the ECG object has no samples or when the beat load status is NONE, otherwise mounts the navigator and AIECGViewer with beat-HR canvas ids, AR beat mode, capture, pick-date, event and bookmark indicators and the update-beat context menu.
 *
 * @param props - Viewer inputs carrying the ECG data map, study and profile ids, timezone offset and the report start and stop bounds.
 * @returns The ECG viewer with its navigator, a loading skeleton, or a no-data message when no beat is displayed.
 */
const BeatEcgViewerWraper = ({
  ecgDataMap,
  studyId,
  profileId,
  timezoneOffset,
  startReport,
  stopReport,
  callbackListDate,
}: BeatEcgViewerWraperProps) => {
  const {
    isLoading,
    isLoadingStrip,
    ecgObject,
    startStripEpoch,
    loadingBeatStatus,
    selectedStrip,
    displayedBeatId,
    navigateStrip,
    disabledNoDataRange,
    ecgChartRef,
    aiChannel,
    pickDateAction,
    eventIndicator,
    bookmarkIndicators,
    handleCaptureImages,
    handlePickDate,
    handleUpdateBeat,
  } = useBeatEcgViewerWrapper({ ecgDataMap, timezoneOffset, callbackListDate });

  if (
    isLoading ||
    isLoadingStrip ||
    !ecgObject?.dataSamples?.length ||
    !startStripEpoch.current ||
    loadingBeatStatus.status === LOAD_BEAT_STATUS.NONE
  ) {
    return (
      <BeatEcgViewerSkeleton
        noData={
          loadingBeatStatus.status === LOAD_BEAT_STATUS.NONE ||
          (_.isEmpty(selectedStrip) && !isLoading)
        }
      />
    );
  }

  return (
    <div id="ecg-viewer" className="ecg-viewer-container">
      {displayedBeatId && (
        <NavigatorContainer
          startReport={startReport}
          stopReport={stopReport}
          displayedBeatId={displayedBeatId}
          navigateStrip={navigateStrip}
          timezoneOffset={timezoneOffset}
          disabledNoDataRange={disabledNoDataRange}
        />
      )}
      {displayedBeatId ? (
        <AIECGViewer
          ref={ecgChartRef}
          idComponents={{
            canvasBackground: 'cvBgBeatHr',
            exportGridCanvas: 'expGridCvBeatHr',
            canvasChart: 'cvChartBeatHr',
            canvasObject: 'cvObjBeatHr',
            canvasScroller: 'cvScrollerBeatHr',
            contextMenu: 'contextMenuBeatHr',
            tooltip: 'tooltipBeatHr',
            tab: BEAT_HR_TAB_ID,
          }}
          ecg={ecgObject}
          ecgDataMap={ecgDataMap}
          configChart={configChart}
          isAppliedAI
          isDisableAIAssist
          isHideFineGridMenu
          isHideMarkASampleMenu
          hasUpdateBeatContextMenu
          handleCaptureImages={handleCaptureImages}
          captureMode={CAPTURE_MODE.OTHER}
          handlePickDate={handlePickDate}
          pickDateAction={pickDateAction}
          eventIndicator={eventIndicator}
          bookmarkIndicators={bookmarkIndicators}
          handleUpdateBeat={handleUpdateBeat}
          timeObject={{
            startMoment: startStripEpoch.current,
            timezoneOffset,
          }}
          isInitialARBeatMode
          aiChannel={aiChannel}
          studyInfoObject={{
            studyId,
            profileId,
            type: EVENT_OTHER_TYPE,
          }}
          isHideViewAllMode
        />
      ) : (
        <div className="ecg-viewer-skeleton-container">
          <div className="chart-container --no-data-content">
            <div className="no-data">There is no ECG data to display.</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BeatEcgViewerWraper;
